import type { Track, TimeRange, AisClass } from '../types/scenario';
import type { AisPosition } from '../types/generated-data';
import type { DataGenerator, ValidationResult } from '../types/generator';
import { interpolatePathWithWaypoints } from './interpolation';
import { aisRealisticSample } from './ais-sampler';
import { applyManipulations } from './apply-manipulations';
import { applyWaypointAnomaly } from './anomaly';

/**
 * AIS data generator. Generates synthetic AIS position reports
 * along a drawn path with configurable sampling.
 */
export class AisGenerator implements DataGenerator<AisPosition> {
  readonly type = 'ais';
  readonly label = 'AIS';

  /** Resolve AIS class from Source, supporting both new aisClass and legacy aisType. */
  private static resolveAisClass(source: Track['source']): AisClass {
    if (source.aisClass) return source.aisClass;
    // Legacy fallback: parse free-text aisType
    if (source.aisType?.toLowerCase().includes('b')) return 'B';
    return 'A';
  }

  validate(track: Track, timeRange?: TimeRange): ValidationResult {
    const errors: string[] = [];

    if (
      !track.path ||
      !track.path.coordinates ||
      track.path.coordinates.length < 2
    ) {
      errors.push('Track path must have at least 2 coordinates');
    }

    if (!track.source.name) {
      errors.push('Source name is required');
    }

    // MMSI/IMO are optional — synthetic data can omit identifiers

    const startMs = new Date(
      timeRange?.start_time ?? track.start_time,
    ).getTime();
    const endMs = new Date(timeRange?.end_time ?? track.end_time).getTime();

    if (isNaN(startMs) || isNaN(endMs)) {
      errors.push(
        'Invalid time range: start_time and end_time must be valid ISO 8601 dates',
      );
    } else if (endMs <= startMs) {
      errors.push('end_time must be after start_time');
    }

    if (track.sampling.mode === 'count' && track.sampling.value < 2) {
      errors.push('Sample count must be at least 2');
    }
    if (
      track.sampling.mode === 'time_interval' &&
      track.sampling.value_s <= 0
    ) {
      errors.push('Time interval must be greater than 0');
    }
    if (
      track.sampling.mode === 'distance_interval' &&
      track.sampling.value_m <= 0
    ) {
      errors.push('Distance interval must be greater than 0');
    }
    if (
      track.sampling.mode === 'original' &&
      track.sampling.fractions.length < 2
    ) {
      errors.push('Original sampling must have at least 2 time fractions');
    }

    return { valid: errors.length === 0, errors };
  }

  generate(track: Track, timeRange?: TimeRange): AisPosition[] {
    const validation = this.validate(track, timeRange);
    if (!validation.valid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const startMs = new Date(
      timeRange?.start_time ?? track.start_time,
    ).getTime();
    const endMs = new Date(timeRange?.end_time ?? track.end_time).getTime();
    const durationS = (endMs - startMs) / 1000;

    const points = interpolatePathWithWaypoints(
      track.path.coordinates,
      track.waypoints,
      track.sampling,
      durationS,
    );

    const aisClass = AisGenerator.resolveAisClass(track.source);

    let positions: AisPosition[];

    // Realistic mode: use the AIS sampler for proper message types and intervals
    if (track.sampling.mode === 'ais_realistic') {
      positions = aisRealisticSample(points, {
        aisClass,
        durationS,
        startMs,
        source: {
          mmsi: track.source.mmsi ?? null,
          imo: track.source.imo ?? null,
          name: track.source.name,
          callSign: track.source.callSign ?? null,
          shipType: track.source.shipType ?? null,
          flag: track.source.flag ?? null,
          width_m: track.source.width_m ?? null,
          length_m: track.source.length_m ?? null,
          draught_m: track.source.draught_m ?? null,
          destination: track.source.destination ?? null,
          eta: track.source.eta ?? null,
        },
      });
    } else {
      // Standard modes: emit all points as position reports (backwards compatible)
      positions = this.buildFlatPositions(
        points,
        track,
        aisClass,
        startMs,
        endMs,
      );
    }

    // Apply non-destructive manipulation effects (signal gap, spoofing, etc.)
    return applyManipulations(
      positions,
      track.manipulations,
      startMs,
      endMs - startMs,
    );
  }

  private buildFlatPositions(
    points: ReturnType<typeof interpolatePathWithWaypoints>,
    track: Track,
    aisClass: AisClass,
    startMs: number,
    endMs: number,
  ): AisPosition[] {
    const positionMsgType = aisClass === 'A' ? 1 : 18;
    const positions: AisPosition[] = [];

    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const timestampMs = startMs + point.fraction * (endMs - startMs);

      const base: AisPosition = {
        message_type: positionMsgType as AisPosition['message_type'],
        // Identity
        mmsi: point.mmsi ?? track.source.mmsi ?? null,
        imo: point.imo ?? track.source.imo ?? null,
        name: point.name ?? track.source.name,
        call_sign: point.callSign ?? track.source.callSign ?? null,
        ship_type: point.shipType ?? track.source.shipType ?? null,
        ais_type: `Class ${point.aisClass ?? aisClass}`,
        flag: point.flag ?? track.source.flag ?? null,
        // Dimensions
        width_m: point.width_m ?? track.source.width_m ?? null,
        length_m: point.length_m ?? track.source.length_m ?? null,
        draught_m: point.draught_m ?? track.source.draught_m ?? null,
        // Kinematics
        timestamp: new Date(timestampMs).toISOString(),
        latitude: Number(point.latitude.toFixed(6)),
        longitude: Number(point.longitude.toFixed(6)),
        speed_over_ground_kn: Number(point.sog_kn.toFixed(1)),
        heading_deg: Number(
          (point.heading_deg ?? point.bearing_deg).toFixed(1),
        ),
        course_over_ground_deg: Number(point.bearing_deg.toFixed(1)),
        rate_of_turn_deg_min: Number(point.rot_deg_min.toFixed(1)),
        // Operational
        nav_status: point.nav_status ?? null,
        destination: point.destination ?? track.source.destination ?? null,
        eta: point.eta ?? track.source.eta ?? null,
        anomaly: null,
      };

      positions.push(applyWaypointAnomaly(base, point.waypointType));
    }

    return positions;
  }
}
